/* eslint-disable */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Spinner,
  ButtonGroup,
  Input,
} from "reactstrap";
import "./../assets/css/LiveTranscript.css";

const token = process.env.REACT_APP_TOKEN_BLOB_UPLOAD;

// ---- utils p/ speakers ----
function parseSpeakersText(text) {
  const map = {};
  const parts = (text || "").split(",");
  for (const p of parts) {
    const [id, ...rest] = p.trim().split(":");
    if (id && rest.length) map[id.trim()] = rest.join(":").trim();
  }
  return map;
}

function labelToId(label) {
  const m = String(label || "")
    .trim()
    .match(/(\d+)/);
  return m ? m[1] : "";
}
function applySpeakerMapToText(text, map) {
  if (!text || !map || Object.keys(map).length === 0) return text;
  return text
    .split("\n")
    .map((line) => {
      const mm = line.match(/^\s*([^:]+):\s*(.*)$/);
      if (!mm) return line;
      const rawLabel = mm[1].trim();
      const rest = mm[2] ?? "";
      const id = labelToId(rawLabel);
      const pretty = id && map[id] ? map[id] : null;
      return pretty ? `${pretty}: ${rest}` : line;
    })
    .join("\n");
}
// ---------------------------

const LiveTranscriptPage2 = ({ fileNameCurrent }) => {
  const [engine, setEngine] = useState("azure"); //  'assembly' | 'azure' | 'deepgram' | 'formatted'
  const [filename, setFilename] = useState(null);
  const [displayText, setDisplayText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [speakersText, setSpeakersText] = useState("");
  const [speakersMap, setSpeakersMap] = useState({});

  const fullTextRef = useRef("");
  const fetchIntervalRef = useRef(null);
  const textEndRef = useRef(null);

  const fetchCurrentFilename = useCallback(async () => {
    try {
      const sas = token?.startsWith("?") ? token : `?${token}`;
      const currentFile =
        engine === "assembly"
          ? "current_assembly.txt"
          : engine === "deepgram"
          ? "current_deepgram.txt"
          : engine === "formatted"
          ? "current_formatted.txt"
          : engine === "azure"
          ? "current_azure.txt"
          : null;

      if (!currentFile) return;

      const res = await fetch(
        `https://devlnxstorage.blob.core.windows.net/abs-storage/transcription/${fileNameCurrent}/${currentFile}${sas}&t=${Date.now()}`
      );

      const name = (await res.text()).trim();
      setFilename(name);
    } catch (err) {
      console.error("Erro ao buscar nome do arquivo atual:", err);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const buildUrl = useCallback(() => {
    const sas = token?.startsWith("?") ? token : `?${token}`;
    if (!filename) return "";
    return `https://devlnxstorage.blob.core.windows.net/abs-storage/${filename}${sas}&t=${Date.now()}`;
  }, [filename]);

  const fetchTranscript = useCallback(async () => {
    if (!filename) return;

    try {
      const res = await fetch(buildUrl());
      const lastMod = await fetchBlobLastModified(buildUrl());
      setLastUpdated(lastMod);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      setLoading(false);
      setError(null);

      const oldText = fullTextRef.current;
      if (text.length > oldText.length) {
        const newChunk = text.slice(oldText.length);
        fullTextRef.current = text;
        setDisplayText((prev) => prev + newChunk);
      } else if (text.length < oldText.length) {
        fullTextRef.current = text;
        setDisplayText(text);
      }
    } catch (err) {
      console.error("Erro ao buscar transcrição:", err);
      setError(null);
      setLoading(false);
    }
  }, [buildUrl, filename]);

  useEffect(() => {
    fetchCurrentFilename();
  }, [fetchCurrentFilename]);

  useEffect(() => {
    fetchTranscript();
    fetchIntervalRef.current = setInterval(fetchTranscript, 1500);
    return () => {
      clearInterval(fetchIntervalRef.current);
    };
  }, [fetchTranscript]);

  // Reset ao trocar engine
  useEffect(() => {
    clearInterval(fetchIntervalRef.current);
    fullTextRef.current = "";
    setDisplayText("");
    setLoading(true);
    fetchCurrentFilename();
  }, [engine, fetchCurrentFilename]);

  useEffect(() => {
    if (textEndRef.current) {
      textEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayText]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const sas = token?.startsWith("?") ? token : `?${token}`;
        const currentFile =
          engine === "assembly"
            ? "current_assembly.txt"
            : engine === "deepgram"
            ? "current_deepgram.txt"
            : engine === "formatted"
            ? "current_formatted.txt"
            : engine === "azure"
            ? "current_azure.txt"
            : null;

        if (!currentFile) return;

        const res = await fetch(
          `https://devlnxstorage.blob.core.windows.net/abs-storage/transcription/${fileNameCurrent}/${currentFile}${sas}&t=${Date.now()}`
        );

        if (!res.ok) return;

        const newFilename = (await res.text()).trim();

        // Se mudou, atualiza o filename (e isso vai acionar os outros efeitos)
        if (newFilename && newFilename !== filename) {
          console.log("📁 Novo arquivo detectado:", newFilename);
          setFilename(newFilename);
          fullTextRef.current = "";
          setDisplayText("");
        }
      } catch (err) {
        console.error("Erro ao verificar atualização do arquivo:", err);
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, filename]);

  const handleDownload = () => {
    const blob = new Blob([fullTextRef.current], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `transcription-${engine}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchBlobLastModified = async (blobUrl) => {
    try {
      const res = await fetch(blobUrl, { method: "HEAD" });
      if (!res.ok) throw new Error("Failed to fetch blob headers");
      const lastMod = res.headers.get("Last-Modified");
      return new Date(lastMod);
    } catch (err) {
      console.error("Erro ao buscar metadata do blob:", err);
      return null;
    }
  };

  // ------------ speakers ------------
  const sas = token?.startsWith("?") ? token : `?${token}`;
  const speakersBlobPath = `transcription/${fileNameCurrent}/speakers.json`;
  const buildSpeakersUrl = useCallback(
    () =>
      `https://devlnxstorage.blob.core.windows.net/abs-storage/${speakersBlobPath}${sas}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileNameCurrent]
  );


  const loadSpeakers = useCallback(async () => {
    try {
      const res = await fetch(`${buildSpeakersUrl()}&t=${Date.now()}`, {
        cache: "no-store",
      });
      if (res.ok) {
        const json = await res.json();
        setSpeakersMap(json || {});
        const oneLine = Object.entries(json || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(", "); // <-- vírgula e espaço
        setSpeakersText(oneLine);
      } else {
        setSpeakersText("");
        setSpeakersMap({});
      }
    } catch {
      setSpeakersText("");
      setSpeakersMap({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNameCurrent]);

  const saveSpeakers = useCallback(async () => {
    const map = parseSpeakersText(speakersText);
    if (Object.keys(map).length === 0) {
      alert("Use o formato: 1: Nome  2: Outro Nome");
      return;
    }
    setSpeakersMap(map);
    try {
      const res = await fetch(buildSpeakersUrl(), {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(map, null, 2),
      });
      if (!res.ok) throw new Error(`PUT speakers.json ${res.status}`);
      console.log("✅ speakers.json salvo");
    } catch (e) {
      console.error("❌ erro ao salvar speakers.json:", e);
      alert(
        "Erro ao salvar speakers.json. Verifique SAS (write) e CORS (PUT/x-ms-blob-type)."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakersText, fileNameCurrent]);

  useEffect(() => {
    loadSpeakers();
  }, [loadSpeakers]);

  useEffect(() => {
    const id = setInterval(() => {
      loadSpeakers();
    }, 15000);
  
    return () => clearInterval(id);
  }, [loadSpeakers]);

  const displayWithNames = useMemo(
    () => applySpeakerMapToText(displayText, speakersMap),
    [displayText, speakersMap]
  );
  // ---------------------------------------------------------------------

  return (
    <Container fluid className="live-transcript-page">
      <Row className="justify-content-center mt-4 mb-4">
        <Col xs="12" md="12" lg="11">
          <Card className="live-transcript-card shadow-sm">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Transcrição ao Vivo</h5>

              <ButtonGroup>
                <Button
                  color={engine === "azure" ? "primary" : "secondary"}
                  onClick={() => setEngine("azure")}
                  active={engine === "azure"}
                >
                  transcription
                </Button>
                <Button
                  color={engine === "formatted" ? "primary" : "secondary"}
                  onClick={() => setEngine("formatted")}
                  active={engine === "formatted"}
                >
                  Formatted
                </Button>
                {/* <Button
                  color={engine === "assembly" ? "primary" : "secondary"}
                  onClick={() => setEngine("assembly")}
                  active={engine === "assembly"}
                >
                  AssemblyAI
                </Button>
                <Button
                  color={engine === "deepgram" ? "primary" : "secondary"}
                  onClick={() => setEngine("deepgram")}
                  active={engine === "deepgram"}
                >
                  Deepgram
                </Button> */}
              </ButtonGroup>
            </CardHeader>

            <div className="px-3 pt-2">
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto justify-content-end">
                <small className="text-muted mr-2">Speakers:</small>
                <Input
                  type="text"
                  value={speakersText}
                  onChange={(e) => setSpeakersText(e.target.value)}
                  placeholder="ex: 1: Nome 1, 2: Nome 2"
                  style={{ width: 360 }}
                />
                <Button className="ml-2 mb-2"  color="primary" onClick={saveSpeakers}>
                  Salvar
                </Button>
              </div>
            </div>

            <CardBody className="live-transcript-body">
              {loading && !displayText ? (
                <div className="live-transcript-loading">
                  <Spinner color="primary" />
                </div>
              ) : error ? (
                <div className="text-danger small">{error}</div>
              ) : (
                <pre className="typed-text">
                  {displayWithNames}
                  <span className="blinking-cursor">|</span>
                  <div ref={textEndRef} />
                </pre>
              )}
            </CardBody>

            <CardFooter className="d-flex justify-content-between align-items-center small text-muted">
              <span>
                {fullTextRef.current.length.toLocaleString()} caracteres
                {lastUpdated && (
                  <>
                    {" "}
                    &middot; Arquivo atualizado em:{" "}
                    {lastUpdated.toLocaleString()}
                  </>
                )}
              </span>
              <Button
                size="sm"
                color="success"
                onClick={handleDownload}
                disabled={loading || !displayText}
              >
                Baixar TXT
              </Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LiveTranscriptPage2;
